import {AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, Renderer2,} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Title} from '@angular/platform-browser';

import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import * as Waves from 'node-waves';

import {CoreMenuService} from '@core/components/core-menu/core-menu.service';
import {CoreSidebarService} from '@core/components/core-sidebar/core-sidebar.service';
import {CoreConfigService} from '@core/services/config.service';
import {CoreTranslationService} from '@core/services/translation.service';

import {menu} from 'app/menu/menu';
import {AuthenticationService, GoogleAnalyticsService, UserService} from './core/service';
import {HttpErrorResponse} from '@angular/common/http';
import {TimeTrackerService} from './core/service/time-tracker.service';
import {NavigationEnd, Router, RoutesRecognized} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CoreMenu} from '@core/types';
import {Capacitor} from '@capacitor/core';
import {App, URLOpenListenerEvent} from '@capacitor/app';
import {isMobile, PRICING_PLAN_LABEL, PricingPlan} from './core/helpers/constants';
import {MobileappversionService} from './core/service/mobileappversion.service';
import {MobileAppVersion} from './core/models/mobile-app-version';
import {compare} from 'compare-versions';
import {MobileAppUpdateComponent} from './main/pages/mobile-app-update/mobile-app-update.component';
import {SelfServiceAdminService} from './core/service/selfServiceAdmin.service';
import {ViewportService} from "./core/service/viewport.service";
import {Browser} from "@capacitor/browser";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  coreConfig: any;
  menu: CoreMenu[];
  defaultLanguage: 'ro'; // This language will be used as a fallback when a translation isn't found in the current language
  appLanguage: 'ro'; // Set application default language i.e fr

  // Private
  private _unsubscribeAll: Subject<void>;
  timeInterval: any;
  private subdomain = 'default';
  public isFlexPlan: boolean;
  // Masks sensitive content in the app-switcher snapshot while the app is backgrounded/inactive
  public isAppInBackground = false;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private modalService: NgbModal,
    private router: Router,
    private userService: UserService,
    private gAnalyticsService: GoogleAnalyticsService,
    private zone: NgZone,
    private minVersionService: MobileappversionService,
    private selfServiceAdminService: SelfServiceAdminService,
    private viewportService: ViewportService
  ) {

    const userlanguage = localStorage.getItem('user_lang') || 'ro';
    this._translateService.onLangChange.subscribe((event) => {
      localStorage.setItem('user_lang', event.lang);
    });

    // Add languages to the translation service
    this._translateService.addLangs(['en', 'fr', 'de', 'pt', 'ro']);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang(userlanguage);

    // Set the translations for the menu
    this._coreTranslationService.setActiveLang(userlanguage);

    // Set application default language.
    // Change application language? Read the ngxTranslate Fix
    this._translateService.use(userlanguage);
    // ? OR
    // ? User the current browser lang if available, if undefined use 'en'
    // const browserLang = this._translateService.getBrowserLang();
    // this._translateService.use(browserLang.match(/en|fr|de|pt/) ? browserLang : 'en');

    /**
     * ! Fix : ngxTranslate
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     *
     * Using different language than the default ('en') one i.e French?
     * In this case, you may find the issue where application is not properly translated when your app is initialized.
     *
     * It's due to ngxTranslate module and below is a fix for that.
     * Eventually we will move to the multi language implementation over to the Angular's core language service.
     *
     **/

    // Set the default language to 'en' and then back to 'fr'.

    setTimeout(() => {
      this._translateService.setDefaultLang('ro');
      this._translateService.setDefaultLang('en');
    });

    /**
     * !Fix: ngxTranslate
     * ----------------------------------------------------------------------------------------------------
     */

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  public registerAndroidListener() {
    App.addListener('backButton', (data) => {
      if (data.canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
  }

  /**
   * Masks sensitive content (e.g. user profile data) with a privacy overlay
   * whenever the app moves to background/inactive, so it isn't exposed in
   * the OS app-switcher snapshot. Unmasks when the app becomes active again.
   */
  public registerAppStateListener() {
    App.addListener('appStateChange', ({isActive}) => {
      this.zone.run(() => {
        this.isAppInBackground = !isActive;
      });
    });
  }

  ngOnInit(): void {
    // Init wave effect (Ripple effect)
    Waves.init();

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;

        // Layout
        // --------

        // Remove default classes first
        this._elementRef.nativeElement.classList.remove(
          'vertical-layout',
          'vertical-menu-modern',
          'horizontal-layout',
          'horizontal-menu'
        );
        // Add class based on config options
        if (this.coreConfig.layout.type === 'vertical') {
          this._elementRef.nativeElement.classList.add(
            'vertical-layout',
            'vertical-menu-modern'
          );
        } else if (this.coreConfig.layout.type === 'horizontal') {
          this._elementRef.nativeElement.classList.add(
            'horizontal-layout',
            'horizontal-menu'
          );
        }

        // Navbar
        // --------

        // Remove default classes first
        this._elementRef.nativeElement.classList.remove(
          'navbar-floating',
          'navbar-static',
          'navbar-sticky',
          'navbar-hidden'
        );

        // Add class based on config options
        if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
          this._elementRef.nativeElement.classList.add('navbar-static');
        } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
          this._elementRef.nativeElement.classList.add('navbar-sticky');
        } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
          this._elementRef.nativeElement.classList.add('navbar-floating');
        } else {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }

        // Footer
        // --------

        // Remove default classes first
        this._elementRef.nativeElement.classList.remove(
          'footer-fixed',
          'footer-static',
          'footer-hidden'
        );

        // Add class based on config options
        if (this.coreConfig.layout.footer.type === 'footer-sticky') {
          this._elementRef.nativeElement.classList.add('footer-fixed');
        } else if (this.coreConfig.layout.footer.type === 'footer-static') {
          this._elementRef.nativeElement.classList.add('footer-static');
        } else {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }

        // Blank layout
        if (
          this.coreConfig.layout.menu.hidden &&
          this.coreConfig.layout.navbar.hidden &&
          this.coreConfig.layout.footer.hidden
        ) {
          this._elementRef.nativeElement.classList.add('blank-page');
          // ! Fix: Transition issue while coming from blank page
          this._renderer.setAttribute(
            this._elementRef.nativeElement.getElementsByClassName(
              'app-content'
            )[0],
            'style',
            'transition:none'
          );
        } else {
          this._elementRef.nativeElement.classList.remove('blank-page');
          // ! Fix: Transition issue while coming from blank page
          setTimeout(() => {
            const appContentElement = this._elementRef.nativeElement.getElementsByClassName(
              'app-content'
            );

            if (appContentElement && appContentElement[0] && appContentElement[0].setAttribute) {
              this._renderer.setAttribute(
                appContentElement[0],
                'style',
                'transition:300ms ease all'
              );
            }
          }, 0);
          // If navbar hidden
          if (this.coreConfig.layout.navbar.hidden) {
            this._elementRef.nativeElement.classList.add('navbar-hidden');
          }
          // Menu (Vertical menu hidden)
          if (this.coreConfig.layout.menu.hidden) {
            this._renderer.setAttribute(
              this._elementRef.nativeElement,
              'data-col',
              '1-column'
            );
          } else {
            this._renderer.removeAttribute(
              this._elementRef.nativeElement,
              'data-col'
            );
          }
          // Footer
          if (this.coreConfig.layout.footer.hidden) {
            this._elementRef.nativeElement.classList.add('footer-hidden');
          }
        }

        // Skin Class (Adding to body as it requires highest priority)
        if (
          this.coreConfig.layout.skin !== '' &&
          this.coreConfig.layout.skin !== undefined
        ) {
          this.document.body.classList.remove(
            'default-layout',
            'bordered-layout',
            'dark-layout',
            'semi-dark-layout'
          );
          this.document.body.classList.add(
            this.coreConfig.layout.skin + '-layout'
          );
        }
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.modalService.dismissAll();
        this.authenticationService.updateCurrentUserInfo();
      }
    });


    if (isMobile()) {

      //fix viewport
      this.viewportService.configureViewport();

      // enable back button on Android
      if (Capacitor.getPlatform() === 'android') {
        this.registerAndroidListener();
      }

      // mask sensitive content when the app is backgrounded/inactive (iOS + Android)
      this.registerAppStateListener();

      // enables deep-linking by navigating direct to url
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(() => {
          const url = event.url;

          if (url.startsWith('mybenefits://')) {
            Browser.close().catch(err => {
              console.log('Browser already closed:', err);
            });
            const path = url.replace('mybenefits://', '/');
            this.router.navigateByUrl(path);
          } else if (url.includes('.ro')) {
            const slug = url.split('.ro').pop();
            if (slug) {
              this.router.navigateByUrl(slug);
            }
          }
        });
      });

      // enables prompt to update
      App.getInfo()
        .then(value => {
          const currVersion = value.version;
          this.minVersionService.getRequiredVersion().subscribe({
            next: minVersion => {
              if (this.promptForUpdate(currVersion, minVersion)) {
                this.modalService.open(MobileAppUpdateComponent, {
                  backdrop: 'static',
                  centered: true
                });
                return;
              }
            }
          });
        })
        .catch(err => {
          // let the app start if we can't read the min version.
        });
    }

    // Set the application page title
    this._title.setTitle(this.coreConfig.app.appTitle);
    this.timeInterval = setInterval(() => {
      this.authenticationService.updateCurrentUserInfo();
    }, 60000);

    this.gAnalyticsService.addGATagToHead();
    this.getUserCompanyInfo();
    this.authenticationService.currentUser.pipe(takeUntil(this._unsubscribeAll)).subscribe(x => {
      this.userService.getUserProfile(x.userId).pipe(takeUntil(this._unsubscribeAll)).subscribe(
        (userInfoResponse) => {
          const userInfo = userInfoResponse.responseObject as any;
          this.gAnalyticsService.addGeneralEventsAnalytics('set', 'user_id', userInfo.id);
          this.gAnalyticsService.addGeneralEventsAnalytics('set', 'user_properties', {
            user_id: userInfo.id ?? '-',
            company_name: userInfo.companyName ?? '-',
            company_id: userInfo.companyId ?? '-',
            role: JSON.stringify(x.roles) ?? '-',
            gender: userInfo.genderId ?? '-',
            tax_deduction: userInfo.taxExemption ?? '-',
            tax_type: userInfo.companyTaxDistribution ?? '-'
          });
        }
      );
    });
  }

  ngAfterViewInit() {
    TimeTrackerService.clearLocalStorage();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    this.timeInterval.clearInterval();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Get user company info
   */
  getUserCompanyInfo() {
    this.router.events.subscribe((val) => {
      if (val instanceof RoutesRecognized) {
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state?.['skipCompanyInfoReload']) {
          return;
        }

        const subdomain = val.state.root.firstChild.params['subdomain'];
        if (subdomain && subdomain !== 'default') {
          this.subdomain = subdomain;
          this.authenticationService.getUserCompanyInfo(subdomain).subscribe(
            (_) => {
              this.onGetUserCompanyInfoSuccess();
            },
            (error) => {
              this.onGetUserCompanyInfoFail(error);
            }
          );
        } else {
          this.menu = menu;

          if (this._coreMenuService.verifyKey('main')) {
            this._coreMenuService.unregister('main');
          }

          // Register the menu to the menu service
          this._coreMenuService.register('main', this.menu);

          // Set the main menu as our current menu
          this._coreMenuService.setCurrentMenu('main');
        }
      }
    });
  }

  public onGetUserCompanyInfoSuccess() {
    if (this.authenticationService.currentUserCompanyInfoValue) {
      this.coreConfig.app.appTitle =
        this.authenticationService.currentUserCompanyInfoValue.companyName +
        ' MyBenefits';
      this.coreConfig.app.appLogoImage =
        this.authenticationService.currentUserCompanyInfoValue.pictureUrl;
      this._title.setTitle(this.coreConfig.app.appTitle);
      document.documentElement.style.setProperty(
        '--main-color',
        this.authenticationService.currentUserCompanyInfoValue.color1
      );
      document.documentElement.style.setProperty(
        '--main-color-rgba',
        this.hexToRgbA(
          this.authenticationService.currentUserCompanyInfoValue.color1
        )
      );
      document.documentElement.style.setProperty(
        '--secondary-color',
        this.authenticationService.currentUserCompanyInfoValue.color2
      );
      document.documentElement.style.setProperty(
        '--secondary-color-rgba',
        this.hexToRgbA(
          this.authenticationService.currentUserCompanyInfoValue.color2
        )
      );

      const plan = this.authenticationService.currentUserCompanyInfoValue.pricingPlan;
      this.isFlexPlan = plan === PricingPlan.FLEX;

      // Get the application main menu
      this.menu = menu.filter(item => item.showIf === 'always' ||
        (item.showIf === 'flexible' && this.authenticationService.currentUserCompanyInfoValue.hasFlexibleBenefits)
        || (item.showIf === 'fixed' && this.authenticationService.currentUserCompanyInfoValue.hasFixedBenefits)
        || (item.showIf === 'cashback' && this.authenticationService.currentUserCompanyInfoValue.hasCashbackBudget)
        || (item.showIf === "flexible_" + PRICING_PLAN_LABEL[plan] && this.authenticationService.currentUserCompanyInfoValue.hasFlexibleBenefits)
        || (item.showIf === "admin" && this.selfServiceAdminService.isEnabled(this.subdomain))
      );

      if (this.selfServiceAdminService.isEnabled(this.subdomain)) {
        const adminMenuEntry = this.menu.find(item => item.id === 'admin');
        const currentAuthUser = this.authenticationService.currentUserValue;
        adminMenuEntry.externalUrl = true;
        adminMenuEntry.openInNewTab = false;
        adminMenuEntry.url = this.selfServiceAdminService.getJumpUrl(currentAuthUser.accessToken, currentAuthUser.refreshToken);
      }

      if (this._coreMenuService.verifyKey('main')) {
        this._coreMenuService.unregister('main');
      }
      // Register the menu to the menu service
      this._coreMenuService.register('main', this.menu);

      // Set the main menu as our current menu
      this._coreMenuService.setCurrentMenu('main');
    }
  }

  public onGetUserCompanyInfoFail(error: HttpErrorResponse) {
    if (error && error.status === 404) {
      this.router.navigate(['default/pages/miscellaneous/not-found'], {replaceUrl: true});
    }
  }

  /**
   * Helpers
   */
  public hexToRgbA(hex) {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      // eslint-disable-next-line no-bitwise
      return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',');
    }
    throw new Error('Bad Hex');
  }

  private promptForUpdate(currVersion: string, minRequiredVersion: MobileAppVersion) {
    if (currVersion) {
      return compare(minRequiredVersion.version, currVersion, '>');
    }
    return false;
  }

  public shouldShowBenchat(): boolean {
    return this.isFlexPlan &&
      !this.router.url.includes('/not-found') &&
      !this.router.url.includes('/not-authorized') &&
      !this.router.url.includes('/no-connectivity');
  }
}
